import { useEffect, useState, type ReactNode } from "react";
import { investors as api, type Investor } from "../../api";
import { Link, useRouter } from "../../router";
import { InvestorProfile } from "./tabs/InvestorProfile";
import { InvestorDocuments } from "./tabs/InvestorDocuments";
import { InvestorActivity } from "./tabs/InvestorActivity";
import { InvestorPortfolioTab } from "./tabs/InvestorPortfolioTab";
import { InvestorOrders } from "./tabs/InvestorOrders";
import { InvestorMembers } from "./tabs/InvestorMembers";
import { InvestorFlows } from "./tabs/InvestorFlows";
import { QpJumpActions } from "./QpJumpActions";
import { ClientTypeIcon } from "../../components/ClientTypeIcon";

type Tab =
  | "profile"
  | "members"
  | "portfolio"
  | "orders"
  | "quantfolio"
  | "documents"
  | "activity";

const TAB_LABELS: Record<Tab, string> = {
  profile: "Profile",
  members: "Members",
  portfolio: "Portfolio",
  orders: "Orders",
  quantfolio: "Quantfolio",
  documents: "Documents",
  activity: "Activity",
};

export function InvestorView({ id, tab }: { id: number; tab: Tab }) {
  const { navigate } = useRouter();
  const [investor, setInvestor] = useState<Investor | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [memberCount, setMemberCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get(id)
      .then((d) => !cancelled && setInvestor(d.investor))
      .catch((e) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Groups show a member count on the Members tab; fetch it once the investor
  // is known to be a group (kept live by the tab's onCountChange).
  const isGroup = investor?.type === "group";
  useEffect(() => {
    if (!isGroup) {
      setMemberCount(null);
      return;
    }
    let cancelled = false;
    api
      .listMembers(id)
      .then((d) => !cancelled && setMemberCount(d.members.length))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isGroup, id]);

  async function handleDelete() {
    setDeleting(true);
    try {
      await api.remove(id);
      navigate("/app/crm");
    } catch (e) {
      setError((e as Error).message);
      setDeleting(false);
    }
  }

  if (error && !investor) {
    return (
      <div className="page">
        <Link to="/app/crm" className="back-link">
          ← All investors
        </Link>
        <div className="banner-error">{error}</div>
      </div>
    );
  }

  if (!investor) {
    return (
      <div className="page">
        <div className="muted">Loading…</div>
      </div>
    );
  }

  return (
    <div className="page">
      <Link to="/app/crm" className="back-link">
        ← All investors
      </Link>

      <header className="page-head">
        <div>
          <span className="eyebrow">Investor</span>
          <h1>
            <ClientTypeIcon type={investor.type} className="title-type-icon" />{" "}
            {investor.name}{" "}
            <span className={`pill stage-${investor.stage}`}>{investor.stage}</span>
            {investor.kyc_status !== "approved" && (
              <span className={`pill kyc-${investor.kyc_status}`}>
                KYC {investor.kyc_status}
              </span>
            )}
          </h1>
        </div>
        <div className="page-head-actions">
          <QpJumpActions investorId={id} />
          <Link to={`/app/crm/${id}/edit`} className="btn btn-ghost">
            Edit
          </Link>
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="btn btn-danger"
          >
            Delete
          </button>
        </div>
      </header>

      <nav className="tabs">
        <TabLink to={`/app/crm/${id}`} active={tab === "profile"}>
          {TAB_LABELS.profile}
        </TabLink>
        {isGroup && (
          <TabLink to={`/app/crm/${id}/members`} active={tab === "members"}>
            {memberCount === null
              ? TAB_LABELS.members
              : `${TAB_LABELS.members} (${memberCount})`}
          </TabLink>
        )}
        <TabLink
          to={`/app/crm/${id}/portfolio`}
          active={tab === "portfolio"}
        >
          {TAB_LABELS.portfolio}
        </TabLink>
        <TabLink to={`/app/crm/${id}/orders`} active={tab === "orders"}>
          {TAB_LABELS.orders}
        </TabLink>
        <TabLink to={`/app/crm/${id}/quantfolio`} active={tab === "quantfolio"}>
          {TAB_LABELS.quantfolio}
        </TabLink>
        <TabLink
          to={`/app/crm/${id}/documents`}
          active={tab === "documents"}
        >
          {TAB_LABELS.documents}
        </TabLink>
        <TabLink to={`/app/crm/${id}/activity`} active={tab === "activity"}>
          {TAB_LABELS.activity}
        </TabLink>
      </nav>

      <div className="tab-content">
        {(tab === "profile" || (tab === "members" && !isGroup)) && (
          <InvestorProfile investor={investor} onComplianceChange={setInvestor} />
        )}
        {tab === "members" && isGroup && (
          <InvestorMembers groupId={id} onCountChange={setMemberCount} />
        )}
        {tab === "portfolio" && <InvestorPortfolioTab investorId={id} />}
        {tab === "orders" && <InvestorOrders investorId={id} />}
        {tab === "quantfolio" && <InvestorFlows investorId={id} />}
        {tab === "documents" && <InvestorDocuments investorId={id} />}
        {tab === "activity" && <InvestorActivity investorId={id} />}
      </div>

      {confirmDelete && (
        <div className="modal-backdrop" onClick={() => setConfirmDelete(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Delete investor?</h2>
            <p>
              <strong>{investor.name}</strong> will be removed permanently,
              along with all their documents and activity. This can't be undone.
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? "Deleting…" : "Delete investor"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TabLink({
  to,
  active,
  children,
}: {
  to: string;
  active: boolean;
  children: ReactNode;
}) {
  return (
    <Link to={to} className={active ? "tab-link active" : "tab-link"}>
      {children}
    </Link>
  );
}
